<template>
  <ChatbotAboutLayout
    title="Quantum Roadmap"
    what-it-is-subtitle="Turn quantum ambition into a concrete plan"
    :description-paragraphs="descriptionParagraphs"
    :features="features"
    audience-subtitle="For companies ready to act on quantum"
    :audience-items="audienceItems"
    :resources="resources"
    try-it-subtitle="Try the chatbot"
    :video="video"
    :embed="embed"
    :embed-intro="embedIntro"
  />
</template>

<script>
import ChatbotAboutLayout from "@/components/ChatbotAboutLayout.vue";

const CHATBOT_URL = "https://staging.hybridintelligence.eu/qroadmap";

export default {
  components: {
    ChatbotAboutLayout,
  },
  setup() {
    const descriptionParagraphs = [
      "Quantum technology is moving from research labs into real business contexts — but many companies struggle to translate interest into a clear, staged plan. The Quantum Roadmap chatbot helps organisations define where quantum fits, what to do first, and how to build momentum over time.",
      "The downloadable PDF follows the same format as the readiness report from the Quantum Readiness Chatbot — we recommend completing that assessment first so your roadmap builds on a structured profile of your organisation. Through a guided conversation, the Roadmap captures your sector, goals, capabilities, and risk appetite, then suggests a tailored action plan with milestones you can share with leadership and technical teams.",
    ];

    const features = [
      {
        title: "Clarify your starting point",
        text: "Describe your industry, use cases of interest, and current quantum maturity so the roadmap reflects your real context — or simply take the ",
        link: {
          label: "readiness assessment",
          to: { name: "aboutQuantumReadinessChatbot" },
        },
        textSuffix: " first.",
      },
      {
        title: "Build a phased action plan",
        text: "Get recommended short-, medium-, and long-term steps — from awareness and partnerships to pilots and capability building.",
      },
      {
        title: "Align teams around next steps",
        text: "Export a structured summary of priorities, owners, and timelines to kick off internal discussions and decision-making.",
      },
    ];

    const audienceItems = [
      {
        title: "Who it's for",
        text: "Business leaders, innovation managers, and strategy teams in companies exploring or scaling quantum initiatives.",
      },
      {
        title: "Best use cases",
        text: "Shaping company quantum strategy, identifying where quantum fits in your product or operations pipeline, aligning leadership and technical teams, and planning phased integration from awareness to pilots.",
      },
      {
        title: "Recurring usefulness",
        text: "Revisit quarterly or when market conditions, partnerships, or internal capabilities shift — refresh your roadmap as you learn.",
      },
    ];

    const resources = [
      {
        label: "Quantum Readiness Chatbot",
        to: { name: "aboutQuantumReadinessChatbot" },
        description:
          "Start here to assess your organisation's quantum readiness — the Roadmap PDF is designed around the report this chatbot generates.",
      },
      {
        label: "European quantum ecosystem overview",
        href: "https://quantumready.eu/",
        description: "Background on Europe's quantum readiness initiatives and community resources.",
      },
    ];

    const video = {
      src: "/videos/qroadmap.mp4",
      type: "video/mp4",
    };

    const embed = {
      src: CHATBOT_URL,
      title: "Quantum Roadmap",
    };

    const embedIntro =
      "Use the embedded chatbot below to build a tailored quantum action plan for your organisation — from first steps to longer-term milestones.";

    return {
      descriptionParagraphs,
      features,
      audienceItems,
      resources,
      video,
      embed,
      embedIntro,
    };
  },
};
</script>
